import PropTypes from 'prop-types';
import React, { useContext, useState, useRef, useEffect } from 'react';
import ReactPlaceholder from 'react-placeholder';
import ThemeContext from '../../providers/ThemeProvider';

const SeoText = ({ text, title, isNewFilterApplied }) => {
  const theme = useContext(ThemeContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (isNewFilterApplied && textRef.current && text) {
      const element = textRef.current;
      const lineHeight = Number.parseFloat(
        window.getComputedStyle(element).lineHeight,
      );
      const maxLines = 3;
      const maxHeight = lineHeight * maxLines;

      if (element.scrollHeight > maxHeight) {
        setShowReadMore(true);
      }
    }
  }, [text, isNewFilterApplied]);

  const toggleReadMore = () => {
    setIsExpanded(!isExpanded);
  };

  const getTextClassName = () => {
    if (!isNewFilterApplied) return 'seo-text';

    return `seo-text ${
      isExpanded ? 'seo-text--expanded' : 'seo-text--truncated'
    }`;
  };

  return (
    <>
      <div className="seo-container">
        <h1 className="seo-title">
          <ReactPlaceholder
            ready={title}
            rows={1}
            style={{ width: 90 }}
            type="text"
          >
            {title}
          </ReactPlaceholder>
        </h1>
        <div className={getTextClassName()} ref={textRef}>
          <ReactPlaceholder ready={text} rows={6} type="text">
            <div dangerouslySetInnerHTML={{ __html: text }} />
          </ReactPlaceholder>
        </div>
        {isNewFilterApplied && showReadMore && (
          <button
            className="read-more-btn"
            onClick={toggleReadMore}
            type="button"
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </button>
        )}
      </div>
      <style jsx>{`
        .seo-container {
          position: relative;
        }
        .seo-title {
          color: ${theme.colors.headingtext};
          font-size: 22px;
          line-height: 22px;
          margin-bottom: 5px;
          padding: 16px 16px 0px 16px;
          text-align: left;
          text-transform: capitalize;
        }
        .seo-text {
          color: ${theme.colors.bodytext};
          font-size: 11px;
          margin-bottom: ${isNewFilterApplied ? '5px' : '15px'};
          padding: 0px 16px;
          text-align: left;
          line-height: 1.25em;
        }
        .seo-text--truncated {
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          max-height: calc(1.25em * 3);
        }
        .seo-text--expanded {
          overflow: visible;
          display: block;
          max-height: none;
        }
        .read-more-btn {
          background: none;
          border: none;
          color: ${theme.colors.primary || '#007bff'};
          cursor: pointer;
          font-size: ${isNewFilterApplied ? '11px' : '14px'};
          padding: 0 16px 15px 16px;
          text-align: left;
          text-decoration: underline;
          transition: color 0.2s ease;
        }
        @media (min-width: ${theme.breakpoints.mdUp}) {
          .seo-title {
            font-size: 26px;
            line-height: 26px;
            padding: 15px 15px 0px 15px;
          }
          .seo-text {
            font-size: 16px;
            margin-bottom: 15px;
            padding: 0px 15px;
          }
          .seo-text--truncated {
            max-height: calc(1.25em * 3);
          }
          .read-more-btn {
            font-size: 16px;
            padding: 0 15px 15px 15px;
          }
        }
        @media (min-width: ${theme.breakpoints.xlUp}) {
          .seo-text {
            font-size: 16px;
          }
          .read-more-btn {
            font-size: 14px;
          }
        }
      `}</style>
    </>
  );
};

export default SeoText;

SeoText.propTypes = {
  isNewFilterApplied: PropTypes.bool,
  text: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

SeoText.defaultProps = {
  isNewFilterApplied: false,
};
